/* eslint-disable new-cap */
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ofActionSuccessful } from '@ngxs/store';
import { defer, map, Observable, of, shareReplay, Subscription, switchMap, tap } from 'rxjs';
import { LoadCertificateTypes } from 'src/app/certificate-admin/state/certificate-types.action';
import { CertificateTypeState } from 'src/app/certificate-admin/state/certificate-types.state';
import {
  LoadAllJobPositionGroups,
  LoadAllJobPositionGroupsForDepartmentAndGroups,
} from 'src/app/competence/state/job-position-group.actions';
import { JobPositionGroupState } from 'src/app/competence/state/job-position-group.state';
import {
  LoadAllCompetenceRoles,
  LoadAllCompetenceRolesForCurrentDepartment,
} from 'src/app/competence/state/competence-roles.actions';
import { CompetenceRolesState } from 'src/app/competence/state/competence-roles.state';
import {
  LoadAllJobPositions,
  LoadAllJobPositionsForDepartment,
} from 'src/app/competence/state/job-position.actions';
import { JobPositionState } from 'src/app/competence/state/job-position.state';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { ClearModuleColumnFilters } from 'src/app/core/state/main-table-filter.actions';
import { MainTableFilterState } from 'src/app/core/state/main-table-filter.state';
import { IDepartmentUser } from 'src/app/department/models/department.model';
import {
  LoadAllDepartments,
  LoadUsersForSelectedDepartment,
} from 'src/app/department/state/department.actions';
import { DepartmentState } from 'src/app/department/state/department.state';
import {
  GetAllMaintenanceComponents,
  GetAllMaintenanceComponentsForTenant,
} from 'src/app/maintenance-admin/state/components-structure.actions';
import { ComponentsStructureState } from 'src/app/maintenance-admin/state/components-structure.state';
import { LoadJobAreas } from 'src/app/maintenance-admin/state/job-area.actions';
import { JobAreaState } from 'src/app/maintenance-admin/state/job-area.state';
import { LoadJobTypes } from 'src/app/maintenance-admin/state/job-type.actions';
import { JobTypeState } from 'src/app/maintenance-admin/state/job-type.state';
import { LoadProjects } from 'src/app/maintenance-user/state/project.actions';
import { ProjectState } from 'src/app/maintenance-user/state/project.state';
import { LoadMedicineCategories } from 'src/app/medicine-admin/state/medicine-category.actions';
import { MedicineCategoryState } from 'src/app/medicine-admin/state/medicine-category.state';
import { LoadAllSeverities } from 'src/app/non-conformance-admin/state/severities/severity.actions';
import { SeverityState } from 'src/app/non-conformance-admin/state/severities/severity.state';
import { LoadAllNonConformanceTypes } from 'src/app/non-conformance-admin/state/types/non-conformance-type.actions';
import { NonConformanceTypesState } from 'src/app/non-conformance-admin/state/types/non-conformance-type.state';
import { LoadAllQuantityTypes } from 'src/app/quantity-type/state/quantity-type.actions';
import { QuantityTypeState } from 'src/app/quantity-type/state/quantity-type.state';
import { StaticDependenciesService } from 'src/app/services/static-dependencies.service';
import { IWorkScheduleTemplate } from 'src/app/work-and-rest-admin/models/work-schedule-template.model';
import { LoadAllWorkScheduleTemplates } from 'src/app/work-and-rest-admin/state/work-schedule-template/work-schedule-template.actions';
import { WorkScheduleTemplateState } from 'src/app/work-and-rest-admin/state/work-schedule-template/work-schedule-template.state';
import { fullName } from '../../functions/full-name';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
  PrintReportFilterModel,
} from '../../models/main-table-filter.model';
import { LoadAllUsersAllTenants } from '../../user-view/state/user.actions';
import { UserState } from '../../user-view/state/user.state';
import {
  MainTableFilterColumnData,
  MainTableFilterOperand,
  MainTableFilterListOption,
  MainTableFilterSectionData,
  MainTableSearchByValueType,
} from '../main-table/main-table-column-filter/main-table-column-filter.component';
import { IdNameSfi } from '../../models/id-name-sfi.model';

const DEPARTMENT_POSITION_GROUPS_OPTIONS_KEY = 'department-position-groups';
const DEPARTMENT_COMPETENCE_ROLES_OPTIONS_KEY = 'department-competence-roles';
const TENANT_COMPETENCE_ROLES_OPTIONS_KEY = 'tenant-competence-roles';
const ALL_DEPARTMENTS_TOKEN = 'all_deps';

type FilterOptionsSourceKey = MainTableFilterModulesEnum | string;

const MODULE_MAP: Record<FilterOptionsSourceKey, any[]> = {
  [MainTableFilterModulesEnum.QuantityTypes]: [
    LoadAllQuantityTypes,
    QuantityTypeState.allQuantityTypes,
  ],
  [MainTableFilterModulesEnum.MedicineCategories]: [
    LoadMedicineCategories,
    MedicineCategoryState.medicineCategoriesForCurrentDepartment,
  ],
  [MainTableFilterModulesEnum.JobTypes]: [LoadJobTypes, JobTypeState.jobTypes],
  [MainTableFilterModulesEnum.JobAreas]: [LoadJobAreas, JobAreaState.jobAreas],
  [MainTableFilterModulesEnum.CertificateTypes]: [
    LoadCertificateTypes,
    CertificateTypeState.certificateTypes,
  ],
  [MainTableFilterModulesEnum.Components]: [
    GetAllMaintenanceComponents,
    ComponentsStructureState.getAllComponentListForDepartment,
    (components: IdNameSfi[]) =>
      components
        .map((c) => ({ label: c.name, value: c.name.replace(`${c.sfiCode} `, '') }))
        .sort((a, b) => a.label.localeCompare(b.label)),
  ],
  [MainTableFilterModulesEnum.ComponentsForTenant]: [
    GetAllMaintenanceComponentsForTenant,
    ComponentsStructureState.getAllComponentListForTenant,
    (components: IdNameSfi[]) =>
      components
        .map((c) => ({ label: `${c.sfiCode} ${c.name}`, value: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
  ],
  [MainTableFilterModulesEnum.JobPositions]: [
    LoadAllJobPositionsForDepartment,
    JobPositionState.getJobPositionsForDepartment,
  ],
  [MainTableFilterModulesEnum.JobPositionsForTenant]: [
    LoadAllJobPositions,
    JobPositionState.getAllJobPositions,
  ],
  [MainTableFilterModulesEnum.PositionGroups]: [
    LoadAllJobPositionGroups,
    JobPositionGroupState.getAllJobPositionGroups,
  ],
  [DEPARTMENT_POSITION_GROUPS_OPTIONS_KEY]: [
    LoadAllJobPositionGroupsForDepartmentAndGroups,
    JobPositionGroupState.getJobPositionGroupsForDepartmentAndGroups,
  ],
  [DEPARTMENT_COMPETENCE_ROLES_OPTIONS_KEY]: [
    LoadAllCompetenceRolesForCurrentDepartment,
    CompetenceRolesState.getAllRolesForCurrentDepartment,
  ],
  [TENANT_COMPETENCE_ROLES_OPTIONS_KEY]: [LoadAllCompetenceRoles, CompetenceRolesState.getAllRoles],
  [MainTableFilterModulesEnum.Severities]: [LoadAllSeverities, SeverityState.getAllSeverities],
  [MainTableFilterModulesEnum.NonConformanceTypes]: [
    LoadAllNonConformanceTypes,
    NonConformanceTypesState.getAllNonConformanceTypes,
  ],
  [MainTableFilterModulesEnum.Departments]: [LoadAllDepartments, DepartmentState.getAllDepartments],
  [MainTableFilterModulesEnum.WorkScheduleTemplates]: [
    LoadAllWorkScheduleTemplates,
    WorkScheduleTemplateState.allWorkScheduleTemplates,
    (scheduleTemplates: IWorkScheduleTemplate[]) =>
      scheduleTemplates
        .map((t) => ({ label: `${t.templateCode} ${t.templateName}`, value: t.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
  ],
  [MainTableFilterModulesEnum.Users]: [LoadAllUsersAllTenants, UserState.getAllUsersFromAllTenants],
  [MainTableFilterModulesEnum.UsersForDepartment]: [
    LoadUsersForSelectedDepartment,
    DepartmentState.getUsersForSelectedDepartment,
    (users: IDepartmentUser[]) =>
      users.map((user) => fullName(user)).sort((a, b) => a.localeCompare(b)),
  ],
  [MainTableFilterModulesEnum.Projects]: [LoadProjects, ProjectState.getProjects],
};

const MODULES_WITHOUT_FILTERS = new Set<FilterOptionsSourceKey>([
  MainTableFilterModulesEnum.JobPositions,
  MainTableFilterModulesEnum.PositionGroups,
  MainTableFilterModulesEnum.Departments,
  MainTableFilterModulesEnum.UsersForDepartment,
  DEPARTMENT_POSITION_GROUPS_OPTIONS_KEY,
  DEPARTMENT_COMPETENCE_ROLES_OPTIONS_KEY,
  TENANT_COMPETENCE_ROLES_OPTIONS_KEY,
]);

interface IEntitySectionProperties {
  positions: string;
  positionGroups: string;
  roles: string;
  users?: string;
}

export type MainTableFilterOrGroupMap = Record<string, string>;

@Injectable({ providedIn: 'root' })
export class MainTableFilteringHelperService {
  // TODO Illia: have I overdone it with statics? try a regular service after release
  static cache: { [key: string]: Observable<MainTableFilterListOption> } = {};
  static subs: { [key: string]: Subscription } = {};
  static appendFilteringParams(
    params: HttpParams,
    currentFilter?: MainTableFilterModel | PrintReportFilterModel,
    columnToKeyMap: Record<string, string> = {},
    orGroupMap: MainTableFilterOrGroupMap = {}
  ): any {
    if (!currentFilter) {
      return params;
    }
    const consolidated = consolidateProperties(currentFilter.searchBy || []);
    consolidated.forEach((searchBy) => {
      // TODO Illia: kind of a hack, need to refactor and pass sort of a filtering value trasnformers/mappers to filtering config
      if (searchBy.property === 'expiryType') {
        const arr = (searchBy.value as any).In as string[];
        if (arr.includes('intervalExpiry') && arr.includes('runtimeExpiry')) {
          // eslint-disable-next-line no-param-reassign
          searchBy = { property: 'expiryType', value: { In: ['both'] } };
        }
      }
      const searchByKey = toTitleCase(columnToKeyMap[searchBy.property] || searchBy.property);
      // eslint-disable-next-line no-param-reassign
      params = params.append(`SearchBy[${searchByKey}]`, JSON.stringify(searchBy.value));

      const orGroup = orGroupMap[searchBy.property];
      if (orGroup) {
        // eslint-disable-next-line no-param-reassign
        params = params.append(`OrGroup[${searchByKey}]`, orGroup);
      }
    });
    return params;
  }

  static getSectionedEntityFilterConfig(
    scope: 'department' | 'tenant',
    properties: IEntitySectionProperties
  ): MainTableFilterColumnData {
    const sections: MainTableFilterSectionData[] = [
      {
        labelKey: 'jobPositionGroups',
        property: properties.positionGroups,
        options: this.getJobPositionGroupOptions(scope),
        operand: MainTableFilterOperand.In,
      },
      {
        labelKey: 'jobPositions',
        property: properties.positions,
        options: this.getJobPositionOptions(scope),
        operand: MainTableFilterOperand.In,
      },
      {
        labelKey: 'roles',
        property: properties.roles,
        options: this.getRoleOptions(scope),
        operand: MainTableFilterOperand.In,
      },
    ];

    if (properties.users) {
      sections.push({
        labelKey: 'users',
        property: properties.users,
        options: this.getDepartmentUserOptions(),
        operand: MainTableFilterOperand.In,
      });
    }

    return {
      operand: MainTableFilterOperand.In,
      options: of([]),
      type: 'text',
      sections,
    };
  }

  static getSectionedEntityFilterSections(
    scope: 'department' | 'tenant',
    properties: IEntitySectionProperties
  ): MainTableFilterSectionData[] {
    return this.getSectionedEntityFilterConfig(scope, properties).sections ?? [];
  }

  static getJobPositionOptions(scope: 'department' | 'tenant'): Observable<string[]> {
    const module =
      scope === 'tenant'
        ? MainTableFilterModulesEnum.JobPositionsForTenant
        : MainTableFilterModulesEnum.JobPositions;
    return this.getOptionsForList(module);
  }

  static getJobPositionGroupOptions(scope: 'department' | 'tenant'): Observable<string[]> {
    const module =
      scope === 'tenant'
        ? MainTableFilterModulesEnum.PositionGroups
        : DEPARTMENT_POSITION_GROUPS_OPTIONS_KEY;
    return this.getOptionsForList(module);
  }

  static getRoleOptions(scope: 'department' | 'tenant'): Observable<string[]> {
    const module =
      scope === 'tenant'
        ? TENANT_COMPETENCE_ROLES_OPTIONS_KEY
        : DEPARTMENT_COMPETENCE_ROLES_OPTIONS_KEY;
    return this.getOptionsForList(module);
  }

  static getDepartmentUserOptions(): Observable<string[]> {
    return defer(() => {
      const departmentId = StaticDependenciesService.store.selectSnapshot(
        (state: any) => state.userSettings?.currentDepartment?.id
      );

      if (!departmentId || departmentId === ALL_DEPARTMENTS_TOKEN) {
        return of([]);
      }

      return this.getOptionsForList(MainTableFilterModulesEnum.UsersForDepartment);
    });
  }

  static getOptionsForList(module: FilterOptionsSourceKey): Observable<string[]> {
    const [action, selector, mapper] = MODULE_MAP[module];
    if (!MainTableFilteringHelperService.cache[module]) {
      MainTableFilteringHelperService.cache[module] = of(null).pipe(
        switchMap(() => {
          const actionClass = MODULES_WITHOUT_FILTERS.has(module)
            ? new action()
            : (() => {
                const filter = new MainTableFilterModel(module as MainTableFilterModulesEnum);
                filter.pageSize = 9999;
                return new action(filter);
              })();
          return StaticDependenciesService.store.dispatch(actionClass);
        }),
        switchMap(() => StaticDependenciesService.store.select(selector) as Observable<any>),
        tap((options) => {
          MainTableFilteringHelperService.cache[module] = of(options);
        }),
        shareReplay(1)
      );
    }
    if (!MainTableFilteringHelperService.subs[module]) {
      MainTableFilteringHelperService.subs[module] = StaticDependenciesService.actions
        .pipe(
          ofActionSuccessful(action),
          tap(() => {
            delete MainTableFilteringHelperService.cache[module];
          })
        )
        .subscribe();
    }
    return this.cache[module].pipe(
      map((items) =>
        mapper
          ? mapper(items)
          : items.map((entity: any) => entity.name).sort((a, b) => a.localeCompare(b))
      )
    );
  }

  static clearModuleColumnFiltersIfPresent(
    module: MainTableFilterModulesEnum,
    column: string
  ): void {
    if (!this.moduleHasColumnFilter(module, column)) {
      return;
    }

    StaticDependenciesService.store.dispatch(new ClearModuleColumnFilters(module, column));
  }

  static clear(): void {
    Object.keys(MainTableFilteringHelperService.cache).forEach((key) => {
      delete MainTableFilteringHelperService.cache[key];
    });
    Object.keys(MainTableFilteringHelperService.subs).forEach((key) => {
      MainTableFilteringHelperService.subs[key].unsubscribe();
      delete MainTableFilteringHelperService.subs[key];
    });
  }

  private static moduleHasColumnFilter(
    module: MainTableFilterModulesEnum,
    column: string
  ): boolean {
    const store = StaticDependenciesService.store;
    const currentFilter = store.selectSnapshot(MainTableFilterState.getCurrentFilterDetails);
    const savedSearch = store.selectSnapshot(MainTableFilterState.getModuleSearchBy(module));
    const savedSorting = store.selectSnapshot(MainTableFilterState.getModuleSorting(module));

    return (
      (currentFilter?.module === module &&
        (currentFilter.orderBy === column ||
          (currentFilter.searchBy ?? []).some((item) => item.property === column))) ||
      (savedSearch ?? []).some((item) => item.property === column) ||
      savedSorting?.orderBy === column
    );
  }
}

interface SearchByRequestBody {
  property: string;
  value: Record<string, MainTableSearchByValueType>;
}

function toTitleCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function consolidateProperties(arr: MainTableFilterSearchByModel[]): SearchByRequestBody[] {
  const temp: Record<string, Record<string, MainTableSearchByValueType>> = {};
  arr.forEach(({ property, operand, value }) => {
    if (!temp[property]) {
      temp[property] = {};
    }
    temp[property][operand] = value;
  });
  return Object.entries(temp).map(([property, value]) => ({ property, value }));
}
